import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useSearchParams } from 'react-router-dom';
import { MdSave, MdClose } from 'react-icons/md';
import PaymentRequestsCard, { PaymentRequest } from './PaymentRequestsCard';
import BuyerSection from './BuyerSection';

type Tab = 'sellers' | 'buyers';

interface AmbassadorRow {
    _id: string;
    name: string;
    email: string;
    affiliateCode: string;
    level1Count: number;
    level2Count: number;
    level1LeadCount: number;
    level2LeadCount: number;
    totalLeadCount: number;
    totalDue: number;
    alreadyPaid: number;
    walletBalance: number;
}

interface CommissionConfig {
    level1: number;
    level2: number;
}

interface FilleulRow {
    _id: string;
    name: string;
    email: string;
    affiliateCode: string | null;
    leadCount: number;
    createdAt: string;
}

interface FilleulModal {
    ambassadorName: string;
    ambassadorId: string;
    level: 1 | 2;
}

const fmt = (n: number) =>
    n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

function FilleulsModal({ modal, onClose }: { modal: FilleulModal; onClose: () => void }) {
    const [rows, setRows] = useState<FilleulRow[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios
            .get(
                `${CONFIG.apiUrl}/admin/affiliate-filleuls/${modal.ambassadorId}?level=${modal.level}`,
                authHeaders()
            )
            .then((res) => setRows(res.data?.data ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [modal.ambassadorId, modal.level]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-navy-800 shadow-xl flex flex-col max-h-[80vh]">
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-navy-600 px-6 py-4">
                    <div>
                        <p className="text-base font-bold text-navy-700 dark:text-white">
                            Filleuls Niveau {modal.level}
                        </p>
                        <p className="text-sm text-gray-500">{modal.ambassadorName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700"
                    >
                        <MdClose className="text-xl" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 px-6 py-4">
                    {loading ? (
                        <div className="py-10 text-center text-gray-500">Chargement...</div>
                    ) : rows.length === 0 ? (
                        <div className="py-10 text-center text-gray-500">Aucun filleul.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    {['Filleul', 'Code affilié', 'Leads payés'].map((h) => (
                                        <th
                                            key={h}
                                            className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400 last:pr-0"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-3 pr-4">
                                            <p className="font-semibold text-navy-700 dark:text-white">
                                                {row.name || '—'}
                                            </p>
                                            <p className="text-xs text-gray-500">{row.email}</p>
                                        </td>
                                        <td className="py-3 pr-4 font-mono text-xs font-semibold text-brand-500">
                                            {row.affiliateCode || '—'}
                                        </td>
                                        <td className="py-3 font-semibold text-navy-700 dark:text-white">
                                            {row.leadCount}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function BackOfficeAffiliate() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab: Tab = searchParams.get('tab') === 'buyers' ? 'buyers' : 'sellers';
    const [rows, setRows] = useState<AmbassadorRow[]>([]);
    const [config, setConfig] = useState<CommissionConfig>({ level1: 0.9, level2: 0.1 });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [modal, setModal] = useState<FilleulModal | null>(null);
    const [payingId, setPayingId] = useState<string | null>(null);
    const [confirmPayId, setConfirmPayId] = useState<string | null>(null);
    const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);

    useEffect(() => {
        axios
            .get(`${CONFIG.apiUrl}/admin/affiliate-overview`, authHeaders())
            .then((res) => {
                setRows(res.data?.data ?? []);
                if (res.data?.config) setConfig(res.data.config);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
        axios
            .get(`${CONFIG.apiUrl}/admin/affiliate-payment-requests`, authHeaders())
            .then((res) => setPaymentRequests(res.data?.data ?? []))
            .catch(() => {});
    }, []);

    const handleSave = async () => {
        setSaveError(null);
        setSaving(true);
        try {
            await axios.put(
                `${CONFIG.apiUrl}/admin/affiliate-commission-config`,
                { level1: config.level1, level2: config.level2 },
                authHeaders()
            );
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch {
            setSaveError('Erreur lors de la sauvegarde.');
        } finally {
            setSaving(false);
        }
    };

    const openModal = (row: AmbassadorRow, level: 1 | 2) => {
        setModal({ ambassadorName: row.name || row.email, ambassadorId: row._id, level });
    };

    const handleMarkPaid = async (ambassadorId: string) => {
        setPayingId(ambassadorId);
        try {
            await axios.post(
                `${CONFIG.apiUrl}/admin/affiliate-mark-paid/${ambassadorId}`,
                {},
                authHeaders()
            );
            setRows((prev) =>
                prev.map((r) =>
                    r._id === ambassadorId ? { ...r, alreadyPaid: r.totalDue } : r
                )
            );
        } catch {
        } finally {
            setPayingId(null);
            setConfirmPayId(null);
        }
    };

    const totalDue = rows.reduce((s, r) => s + r.totalDue, 0);
    const totalPaid = rows.reduce((s, r) => s + r.alreadyPaid, 0);
    const totalLeads = rows.reduce((s, r) => s + r.totalLeadCount, 0);
    const remaining = totalDue - totalPaid;

    const sellerRequests = paymentRequests.filter((r) => r.userType !== 'buyer');
    const buyerRequests = paymentRequests.filter((r) => r.userType === 'buyer');
    const handleMarkedSent = (userId: string) => setPaymentRequests((prev) => prev.filter((r) => r.userId !== userId));

    const tabs: { key: Tab; label: string; pending: number }[] = [
        { key: 'sellers', label: 'Vendeurs', pending: sellerRequests.length },
        { key: 'buyers', label: 'Acheteurs', pending: buyerRequests.length },
    ];

    return (
        <div className="pt-[50px] flex flex-col gap-5">
            {/* Seller / buyer program tabs */}
            <div className="flex gap-1 self-start rounded-xl border border-gray-200 bg-white p-1 dark:border-navy-600 dark:bg-navy-800">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setSearchParams(t.key === 'buyers' ? { tab: 'buyers' } : {})}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                            tab === t.key ? 'bg-brand-500 text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700'
                        }`}
                    >
                        {t.label}
                        {t.pending > 0 && (
                            <span className="rounded-full bg-orange-500 px-1.5 text-[11px] font-bold text-white" title="Demandes de virement en attente">
                                {t.pending}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {tab === 'buyers' ? (
                <BuyerSection paymentRequests={buyerRequests} onMarkedSent={handleMarkedSent} />
            ) : (
            <>
            {modal && <FilleulsModal modal={modal} onClose={() => setModal(null)} />}

            {/* KPI row */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                {[
                    { label: 'Ambassadeurs', value: loading ? '—' : String(rows.length) },
                    { label: 'Leads générés', value: loading ? '—' : String(totalLeads) },
                    { label: 'Total dû', value: loading ? '—' : fmt(totalDue) },
                    { label: 'Déjà crédité', value: loading ? '—' : fmt(totalPaid) },
                ].map((kpi) => (
                    <Card key={kpi.label} extra="p-4">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                            {kpi.label}
                        </p>
                        <p className="mt-1 text-2xl font-bold text-navy-700 dark:text-white">
                            {kpi.value}
                        </p>
                    </Card>
                ))}
            </div>

            {/* Commission config */}
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-xl font-bold text-navy-700 dark:text-white">
                            Commissions
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                            Montant versé à l'ambassadeur pour chaque lead payé par ses filleuls.
                        </p>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        <MdSave className="text-lg" />
                        {saving ? 'Sauvegarde...' : saved ? 'Sauvegardé !' : 'Sauvegarder'}
                    </button>
                </div>

                {saveError && (
                    <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
                        {saveError}
                    </div>
                )}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-lg">
                    {([
                        { key: 'level1' as const, label: 'Niveau 1 — filleul direct', desc: 'Par lead payé par un vendeur directement référé.' },
                        { key: 'level2' as const, label: 'Niveau 2 — filleul indirect', desc: 'Par lead payé par un vendeur référé par un filleul.' },
                    ] as const).map(({ key, label, desc }) => (
                        <div key={key} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <label className="block text-xs font-bold uppercase tracking-wide text-gray-600 mb-1">
                                {label}
                            </label>
                            <p className="mb-3 text-xs text-gray-500">{desc}</p>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={config[key]}
                                onChange={(e) =>
                                    setConfig((c) => ({ ...c, [key]: parseFloat(e.target.value) || 0 }))
                                }
                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                            />
                        </div>
                    ))}
                </div>
            </Card>

            <PaymentRequestsCard requests={sellerRequests} onMarkedSent={handleMarkedSent} />

            {/* Ambassador table */}
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xl font-bold text-navy-700 dark:text-white">
                        Ambassadeurs
                    </p>
                    {!loading && rows.length > 0 && (
                        <div className="text-sm font-semibold text-navy-700 dark:text-white">
                            Reste à créditer :{' '}
                            <span className={remaining > 0 ? 'text-orange-500' : 'text-green-600'}>
                                {fmt(remaining)}
                            </span>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Chargement...</div>
                ) : rows.length === 0 ? (
                    <div className="py-10 text-center text-gray-500">Aucun ambassadeur pour le moment.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    {[
                                        'Ambassadeur',
                                        'Code',
                                        'N1 filleuls',
                                        'N2 filleuls',
                                        'Leads N1',
                                        'Leads N2',
                                        'Total leads',
                                        'Total dû',
                                        'Crédité wallet',
                                        'Solde wallet',
                                        'Dette',
                                        '',
                                    ].map((h) => (
                                        <th
                                            key={h}
                                            className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400 last:pr-0"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-3 pr-4">
                                            <p className="font-semibold text-navy-700 dark:text-white">
                                                {row.name || '—'}
                                            </p>
                                            <p className="text-xs text-gray-500">{row.email}</p>
                                        </td>
                                        <td className="py-3 pr-4 font-mono text-xs font-semibold text-brand-500">
                                            {row.affiliateCode}
                                        </td>
                                        <td className="py-3 pr-4">
                                            {row.level1Count > 0 ? (
                                                <button
                                                    onClick={() => openModal(row, 1)}
                                                    className="font-semibold text-brand-500 hover:text-brand-600 underline underline-offset-2"
                                                >
                                                    {row.level1Count}
                                                </button>
                                            ) : (
                                                <span className="text-gray-400">0</span>
                                            )}
                                        </td>
                                        <td className="py-3 pr-4">
                                            {row.level2Count > 0 ? (
                                                <button
                                                    onClick={() => openModal(row, 2)}
                                                    className="font-semibold text-brand-500 hover:text-brand-600 underline underline-offset-2"
                                                >
                                                    {row.level2Count}
                                                </button>
                                            ) : (
                                                <span className="text-gray-400">0</span>
                                            )}
                                        </td>
                                        <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">{row.level1LeadCount}</td>
                                        <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">{row.level2LeadCount}</td>
                                        <td className="py-3 pr-4 font-semibold text-navy-700 dark:text-white">{row.totalLeadCount}</td>
                                        <td className="py-3 pr-4 font-semibold text-navy-700 dark:text-white">{fmt(row.totalDue)}</td>
                                        <td className="py-3 pr-4">
                                            <span className={row.alreadyPaid >= row.totalDue ? 'font-semibold text-green-600' : 'font-semibold text-orange-500'}>
                                                {fmt(row.alreadyPaid)}
                                            </span>
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">{fmt(row.walletBalance)}</td>
                                        <td className="py-3 pr-4">
                                            {(() => {
                                                const debt = Number((row.totalDue - row.alreadyPaid).toFixed(2));
                                                return debt > 0 ? (
                                                    <span className="font-semibold text-red-500">{fmt(debt)}</span>
                                                ) : (
                                                    <span className="font-semibold text-green-600">0,00 €</span>
                                                );
                                            })()}
                                        </td>
                                        <td className="py-3">
                                            {(() => {
                                                const debt = Number((row.totalDue - row.alreadyPaid).toFixed(2));
                                                if (debt <= 0) return null;
                                                if (confirmPayId === row._id) {
                                                    return (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-gray-500">Confirmer ?</span>
                                                            <button
                                                                onClick={() => handleMarkPaid(row._id)}
                                                                disabled={payingId === row._id}
                                                                className="rounded-md bg-green-500 px-2 py-1 text-xs font-medium text-white hover:bg-green-600 disabled:opacity-50"
                                                            >
                                                                {payingId === row._id ? '...' : 'Oui'}
                                                            </button>
                                                            <button
                                                                onClick={() => setConfirmPayId(null)}
                                                                className="rounded-md bg-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-300"
                                                            >
                                                                Non
                                                            </button>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <button
                                                        onClick={() => setConfirmPayId(row._id)}
                                                        className="rounded-md bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 whitespace-nowrap"
                                                    >
                                                        Marquer payé
                                                    </button>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
            </>
            )}
        </div>
    );
}
